"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, ShieldCheck, AlertCircle } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + 100% gestochen scharfe Chat-Bubbles im Vordergrund:
 * Kein rotateX/rotateY/translateZ auf dem Text, damit der Browser volle Subpixel-Schärfe rendert!
 */
export default function Preview() {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const durations = [1100, 1800, 2900];
    const timer = setTimeout(() => {
      setPhase((prev) => (prev + 1) % 3);
    }, durations[phase]);
    return () => clearTimeout(timer);
  }, [phase]);

  const isVerified = phase === 2;

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl px-4 py-3 select-none"
      style={{ perspective: "900px" }}
    >
      {/* 1. Dynamisches Volumetric-Spotlight */}
      <motion.div
        animate={{
          scale: [1, 1.22, 1],
          opacity: [0.5, 0.85, 0.5],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-44 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-colors duration-700 ${
          phase === 1
            ? "bg-rose-500/35 dark:bg-rose-500/30"
            : isVerified
              ? "bg-emerald-500/35 dark:bg-emerald-500/30"
              : "bg-purple-500/25 dark:bg-purple-500/20"
        }`}
      />

      {/* 2. Gekipptes 3D-Boden-Raster */}
      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* 3. Gestochen scharfe Chat-Bubbles (aufrecht, kein 3D-Blur) */}
      <div className="relative z-10 mx-auto flex w-full max-w-[260px] flex-col gap-2.5 antialiased">
        {/* User Chat-Bubble (Rechts) */}
        <div className="ml-auto rounded-2xl rounded-tr-xs border border-purple-300/40 bg-gradient-to-br from-purple-600 to-indigo-600 px-3.5 py-1.5 text-xs font-extrabold text-white shadow-[0_12px_25px_-4px_rgba(147,51,234,0.55)]">
          Was ist 2 + 2?
        </div>

        {/* AI Chat-Bubble (Links) */}
        <div className="min-h-[72px]">
          <AnimatePresence mode="wait">
            {phase === 0 ? (
              <motion.div
                key="typing"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="mr-auto flex w-fit items-center gap-2 rounded-2xl rounded-tl-xs border border-purple-500/30 bg-white px-4 py-2.5 shadow-xl dark:bg-zinc-900"
              >
                <Bot className="h-4 w-4 text-purple-500" />
                <div className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-purple-400 [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-purple-400 [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-purple-400" />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="answer"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 24 }}
                className={`relative mr-auto flex w-full max-w-[245px] flex-col gap-1.5 overflow-hidden rounded-2xl rounded-tl-xs border bg-white px-3.5 py-2.5 transition-all duration-500 dark:bg-zinc-900 ${
                  isVerified
                    ? "border-emerald-400/80 shadow-[0_16px_36px_-6px_rgba(16,185,129,0.55)]"
                    : "border-rose-400/80 shadow-[0_16px_36px_-6px_rgba(244,63,94,0.45)]"
                }`}
              >
                {/* Grüner Verifikations-Laser-Strahl */}
                {isVerified && (
                  <motion.div
                    initial={{ top: "-10%", opacity: 1 }}
                    animate={{ top: "110%", opacity: 0 }}
                    transition={{ duration: 0.65, ease: "easeInOut" }}
                    className="pointer-events-none absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_14px_#10b981]"
                  />
                )}

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-neutral-900 dark:text-white">
                    <Bot
                      className={`h-4 w-4 shrink-0 ${
                        isVerified ? "text-emerald-500" : "text-rose-500"
                      }`}
                    />
                    <span className="flex items-center gap-1.5">
                      <span>Natürlich</span>
                      <span
                        className={`font-mono text-sm font-black transition-all duration-300 ${
                          isVerified
                            ? "text-rose-500 line-through decoration-2 opacity-50"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        5!
                      </span>
                      {!isVerified && <span>🎉</span>}
                    </span>

                    <AnimatePresence>
                      {isVerified && (
                        <motion.span
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="inline-flex items-center rounded-lg bg-emerald-500 px-2 py-0.5 font-mono text-xs font-black text-white shadow-[0_0_16px_rgba(16,185,129,0.85)]"
                        >
                          4 ✓
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Prüf-Zeile */}
                <div className="flex items-center justify-between border-t border-black/5 pt-1 text-[10px] dark:border-white/10">
                  {isVerified ? (
                    <span className="flex items-center gap-1 font-mono font-extrabold text-emerald-600 dark:text-emerald-400">
                      <ShieldCheck className="h-3 w-3" />
                      Gegenprobe: 4 − 2 = 2
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 font-mono font-bold text-rose-500 dark:text-rose-400">
                      <AlertCircle className="h-3 w-3" />
                      Halluziniert (ohne Probe)
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
